import { Builder, withChildren } from '@builder.io/react'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, useRef } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

const CustomBuilderWrapper = (props) => {
  const { 
    children, 
    wrapperClasses = '', 
    hideOnDomains = '',
    hideOnLanguages = '',
    category = '',
    isReadMore = false,
    readMoreHeight = '800px',
    readMoreHeightMobile = '',
    buttonPosition = 'center',
    buttonSharpEdge = false,
    readMoreLabel = 'Read more',
    readLessLabel = 'Read less'
  } = props

  const { i18n } = useTranslation()
  const [currentSection, setCurrentSection] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const [forceRecalculate, setForceRecalculate] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const contentRef = useRef(null)
  
  // Listen for localStorage changes (page-specific)
  useEffect(() => {
    const handleStorageChange = () => {
      const storageKey = `SECTIONS_TOGGLE_${window.location.pathname}`;
      const section = localStorage.getItem(storageKey) || '';
      setCurrentSection(section);
      // Reset read more to initial state when section changes
      setIsExpanded(false);
    };

    // Get initial value
    handleStorageChange();

    // Listen for storage events (when localStorage changes in other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom events (when localStorage changes in same tab)
    window.addEventListener('localStorageChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleStorageChange);
    };
  }, []);

  // Custom event dispatcher for same-tab localStorage changes
  useEffect(() => {
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      if (key === 'SECTIONS_TOGGLE') {
        window.dispatchEvent(new Event('localStorageChange'));
      }
    };
  }, []);

  // Debug effect for read more functionality
  useEffect(() => {
    if (isReadMore) {
      console.log('Read more enabled, contentRef:', contentRef.current, 'isExpanded:', isExpanded)
    }
  }, [isReadMore, isExpanded])

  // Reset read more state when section changes
  useEffect(() => {
    if (isReadMore) {
      setIsExpanded(false);
      console.log('Section changed, resetting read more to collapsed state');
    }
  }, [currentSection, isReadMore]);

  // ResizeObserver to detect child height changes
  useEffect(() => {
    if (!isReadMore || !contentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // Force recalculation when child content height changes
        setForceRecalculate(prev => prev + 1);
      }
    });

    // Observe all child elements for size changes
    const childElements = contentRef.current.querySelectorAll('*');
    childElements.forEach(child => {
      resizeObserver.observe(child);
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [isReadMore, isExpanded]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, []);
  
  try {
    const lang = i18n?.language || 'en'
    const currentHost = window.location?.host?.toLowerCase() || ''

    // Parse arrays from CMS inputs with error handling
    const hideDomainsArray = (hideOnDomains || '')
      .split(',')
      .map((domain) => domain?.trim()?.toLowerCase())
      .filter(Boolean)

    const hideLanguagesArray = (hideOnLanguages || '')
      .split(',')
      .map((language) => language?.trim()?.toLowerCase())
      .filter(Boolean)

    // Hide if current domain or language is in the hide lists
    if (hideDomainsArray.includes(currentHost) || hideLanguagesArray.includes(lang.toLowerCase())) {
      return null
    }

    // Check if category filter is set and matches localStorage
    if (category) {
      if (currentSection.toLowerCase() !== category.toLowerCase()) {
        return null;
      }
    }

    // Read more functionality
    const handleToggleReadMore = () => {
      console.log('Toggle read more clicked, current state:', isExpanded, 'contentRef:', contentRef.current)
      setIsExpanded(!isExpanded)
    }

    const getMaxHeight = () => {
      if (!isReadMore) return 'none'
      if (isExpanded && contentRef.current) {
        // Force recalculation by accessing forceRecalculate
        const scrollHeight = contentRef.current.scrollHeight
        return scrollHeight > 0 ? `${scrollHeight}px` : 'none'
      }
      // Use mobile height if provided and on mobile, otherwise use desktop height
      return (isMobile && readMoreHeightMobile) ? readMoreHeightMobile : readMoreHeight
    }

    if (isReadMore) {
      try {
        const getButtonPositionClasses = () => {
          switch (buttonPosition) {
            case 'left':
              return 'justify-start'
            case 'right':
              return 'justify-end'
            case 'center':
            default:
              return 'justify-center'
          }
        }

        const maxHeight = getMaxHeight()
        console.log('Rendering read more with maxHeight:', maxHeight, 'isExpanded:', isExpanded, 'forceRecalculate:', forceRecalculate)

        return (
          <div className={wrapperClasses}>
            <div 
              ref={contentRef}
              className="overflow-hidden transition-all duration-200 ease-out"
              style={{ maxHeight: maxHeight }}
            >
              {children}
            </div>
            <div className={`flex ${getButtonPositionClasses()} mt-4`}>
              <button 
                onClick={handleToggleReadMore}
                className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-[#1E272E] hover:text-[#1E272E] hover:bg-gray-50 transition-all duration-200 font-medium ${buttonSharpEdge ? '' : 'rounded-md'}`}
              >
                <span>{isExpanded ? readLessLabel : readMoreLabel}</span>
                {isExpanded ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        )
      } catch (readMoreError) {
        console.error('Error in read more functionality:', readMoreError)
        // Fallback to normal rendering if read more fails
        return <div className={wrapperClasses}>{children}</div>
      }
    }

    return <div className={wrapperClasses}>{children}</div>
  } catch (error) {
    console.error('CustomBuilderWrapper error:', error)
    // Fallback: show the component if there's an error
    return <div className={wrapperClasses}>{children}</div>
  }
}

const WrapperWithChildren = withChildren(CustomBuilderWrapper)

Builder.registerComponent(WrapperWithChildren, {
  name: 'CustomBuilderWrapper',
  canHaveChildren: true,
  inputs: [
    {
      name: 'wrapperClasses',
      type: 'string',
      friendlyName: 'Tailwind Classes for Wrapper',
    },
    {
      name: 'hideOnDomains',
      type: 'string',
      friendlyName: 'Hide on Domains (comma-separated)',
      helperText: 'Enter comma-separated domains (e.g., matwproject.org,matwproject.org.uk) where this component should be hidden.',
    },
         {
       name: 'hideOnLanguages',
       type: 'string',
       friendlyName: 'Hide on Languages (comma-separated)',
       helperText: 'Enter comma-separated language codes (e.g., fr,ar,de) where this component should be hidden.',
     },
     {
       name: 'category',
       type: 'string',
       friendlyName: 'Category Filter',
       helperText: 'Only show this content when this category is selected (e.g., water, gaza, lebanon). Leave empty to show always.',
     },
     {
       name: 'isReadMore',
       type: 'boolean',
       defaultValue: false,
       friendlyName: 'Enable Read More',
       helperText: 'Toggle to enable read more functionality with collapsible content.',
     },
    {
      name: 'readMoreHeight',
      type: 'string',
      defaultValue: '800px',
      friendlyName: 'Read More Height (Desktop)',
      helperText: 'Maximum height before content is collapsed on desktop (e.g., 800px, 600px).',
    },
    {
      name: 'readMoreHeightMobile',
      type: 'string',
      defaultValue: '',
      friendlyName: 'Read More Height (Mobile)',
      helperText: 'Maximum height before content is collapsed on mobile (e.g., 400px, 300px). Leave empty to use desktop height.',
    },
           {
        name: 'buttonPosition',
        type: 'enum',
        friendlyName: 'Button Position',
        helperText: 'Choose where to position the read more button.',
        enum: ['left', 'center', 'right'],
        enumLabels: ['Left', 'Center', 'Right'],
        defaultValue: 'center',
      },
      {
        name: 'buttonSharpEdge',
        type: 'boolean',
        defaultValue: false,
        friendlyName: 'Button Sharp Edges',
        helperText: 'Toggle to remove rounded corners from the read more button.',
      },
      {
        name: 'readMoreLabel',
        type: 'string',
        defaultValue: 'Read more',
        friendlyName: 'Read More Label',
        helperText: 'Text to display on the button when content is collapsed.',
      },
      {
        name: 'readLessLabel',
        type: 'string',
        defaultValue: 'Read less',
        friendlyName: 'Read Less Label',
        helperText: 'Text to display on the button when content is expanded.',
      },
  ],
  defaultChildren: [
    {
      '@type': '@builder.io/sdk:Element',
      component: {
        name: 'Text',
        options: {
          text: 'Custom Builder Wrapper',
        },
      },
    },
  ],
})

export default CustomBuilderWrapper